<template>
  <button
    :class="buttonClasses"
    :type="type"
    :disabled="isDisabled || isLoading"
    @click="handleClick"
  >
    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center" role="status">
      <svg
        class="animate-spin -ml-1 mr-3 h-5 w-5 text-current"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <div class="w-1"></div>
      <span>{{ loadingText || $t("wait") }}</span>
    </div>

    <!-- Normal state -->
    <div v-else class="flex items-center justify-center">
      <i v-if="icon" :class="['mr-2', icon]"></i>
      <span>{{ label }}</span>
      <i v-if="rightIcon" :class="['ml-2', rightIcon]"></i>
    </div>
  </button>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "Submit",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "button",
    validator: (value) => ["button", "submit", "reset"].includes(value),
  },
  variant: {
    type: String,
    default: "primary",
    validator: (value) => ["primary", "secondary", "outline", "ghost", "danger"].includes(value),
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg", "xl"].includes(value),
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: "",
  },
  rightIcon: {
    type: String,
    default: "",
  },
  loadingText: {
    type: String,
    default: "",
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["click"]);

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-6 py-2.5 text-sm",
    lg: "px-8 py-3 text-base",
    xl: "px-10 py-4 text-lg",
  };
  return sizes[props.size];
});

// Variant classes
const variantClasses = computed(() => {
  const variants = {
    primary: "bg-primaryColor hover:bg-blue-700 text-white shadow-sm hover:shadow-md",
    secondary: "bg-gray-600 hover:bg-gray-700 text-white shadow-sm hover:shadow-md",
    outline: "border border-gray-300 dark:border-gray-600 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300",
    ghost: "bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300",
    danger: "bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow-md",
  };
  return variants[props.variant];
});

// Button classes
const buttonClasses = computed(() => {
  const baseClasses = [
    "inline-flex items-center justify-center font-medium rounded-lg",
    "transition-all duration-200 ease-in-out",
    "focus:outline-none focus:ring-2 focus:ring-offset-2",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "active:transform active:scale-95",
    sizeClasses.value,
    variantClasses.value,
  ];

  // Focus ring color based on variant
  if (props.variant === "primary" || props.variant === "danger") {
    baseClasses.push("focus:ring-blue-500");
  } else if (props.variant === "secondary") {
    baseClasses.push("focus:ring-gray-500");
  } else {
    baseClasses.push("focus:ring-gray-400");
  }

  // Full width
  if (props.fullWidth) {
    baseClasses.push("w-full");
  }

  return baseClasses.join(" ");
});

// Event handlers
const handleClick = (e) => {
  if (!props.isDisabled && !props.isLoading) {
    emit("click", e);
  }
};
</script>

<style scoped>
/* Button hover effects */
button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:active:not(:disabled) {
  transform: translateY(0);
}

/* Loading animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Focus ring animation */
button:focus {
  animation: focusPulse 0.3s ease-out;
}

@keyframes focusPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}

/* Disabled state */
button:disabled {
  transform: none !important;
  cursor: not-allowed;
}

/* Icon transitions */
i {
  transition: all 0.2s ease-in-out;
}

button:hover:not(:disabled) i {
  transform: scale(1.1);
}
</style>
